import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Box } from "@mui/system";
import { Button, Chip, LinearProgress, Stack } from "@mui/material";
import {
  deleteProduct,
  getAllProducts,
} from "../../../redux/slices/adminProductsSlice";
import { getMe } from "../../../redux/slices/userSlice";
import AddEditProductModal from "./AddEditProductModal";
import AlertDialogComponent from "../../../components/AlertDialogComponent";
import { setNotification } from "../../../redux/slices/notificationsSlice";

const ArticlesSettingTable = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showAddNewArticleModal, setShowAddNewArticleModal] = useState(false);
  const [selectedProductToEdit, setSelectedProductToEdit] = useState();
  const [selectedProductToDelete, setSelectedProductToDelete] = useState();
  const [showDeleteWraningModal, setShowDeleteWarningModal] = useState(false);

  const columns = [
    {
      id: "name",
      label: t("adminSettings.name"),
      minWidth: 100,
    },
    {
      id: "points",
      label: t("adminSettings.points"),
      minWidth: 70,
    },
    {
      id: "alias",
      label: t("adminSettings.alias"),
      minWidth: 170,
    },
    {
      id: "picture",
      label: t("adminSettings.picture"),
    },
    {
      id: "action",
      label: t("adminSettings.actions"),
    },
  ];
  useEffect(() => {
    dispatch(getMe()).then(() => dispatch(getAllProducts()));
  }, [dispatch]);

  const allDefinedProducts = useSelector(
    (state) => state.adminProducts.allDefinedProducts
  );
  const getAllProductsLoading = useSelector(
    (state) => state.adminProducts.getAllProductsLoading
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const onEditProduct = (product) => {
    setSelectedProductToEdit(product);
    setShowAddNewArticleModal(true);
  };
  const onDeleteProduct = (product) => {
    setSelectedProductToDelete(product);
    setShowDeleteWarningModal(true);
  };
  const onShowAddArticleModal = () => {
    setSelectedProductToEdit(null);
    setShowAddNewArticleModal(true);
  };
  const handleOnCloseAddNewArticleModal = () => {
    setShowAddNewArticleModal(false);
  };
  const handleSuccessAddNewProduct = () => {
    setShowAddNewArticleModal(false);
    dispatch(getMe()).then(() => dispatch(getAllProducts()));
  };
  const handleSuccessEditProduct = () => {
    setShowAddNewArticleModal(false);
    setSelectedProductToEdit(null);
    dispatch(getMe()).then(() => dispatch(getAllProducts()));
  };
  const onConfirmDeleteProduct = async () => {
    await dispatch(getMe()).then(() => {
      dispatch(
        deleteProduct({ objectId: selectedProductToDelete.objectId })
      ).then(() => {
        dispatch(getAllProducts());
        setShowDeleteWarningModal(false);
        dispatch(
          setNotification({
            show: true,
            message: t("notifications.successDeleteProduct"),
            severity: "success",
          })
        );
      });
    });
  };
  const handleOnCloseWarningModal = () => {
    setShowDeleteWarningModal(false);
  };

  const getColumn = (value, column) => {
    const isActionColumn = column.id === "action";
    let content = null;

    if (isActionColumn) {
      content = (
        <TableCell key={column.id} align={column.align}>
          <Stack direction="row">
            <Button variant="contained" onClick={() => onEditProduct(value)}>
              {t("adminSettings.edit")}
            </Button>
            <Button
              sx={{ marginLeft: "8px" }}
              onClick={() => onDeleteProduct(value)}
              color="error"
            >
              {t("adminSettings.delete")}
            </Button>
          </Stack>
        </TableCell>
      );
    } else if (column.id === "picture") {
      content = (
        <TableCell key={column.id} align={column.align}>
          {value?.url ? (
            <img src={value?.url} alt="prize-img" width="150px" />
          ) : (
            "n/a"
          )}
        </TableCell>
      );
    } else if (column.id === "alias") {
      content = (
        <TableCell key={column.id} align={column.align}>
          <Stack direction="column">
            {value?.map((alias) => {
              return (
                <Chip
                  color="error"
                  sx={{ marginBottom: "6px" }}
                  label={alias}
                />
              );
            })}
          </Stack>
        </TableCell>
      );
    } else {
      content = (
        <TableCell key={column.id} align={column.align}>
          {value}
        </TableCell>
      );
    }

    return (
      <TableCell key={column.id} align={column.align}>
        {content}
      </TableCell>
    );
  };
  return (
    <>
      <Stack display="flex" direction="row" justifyContent="end">
        <Button
          variant="contained"
          size="small"
          onClick={onShowAddArticleModal}
        >
          {t("adminSettings.addNewArticle")}
        </Button>
      </Stack>
      <Paper sx={{ width: "100%", overflow: "hidden", marginTop: "12px" }}>
        <TableContainer sx={{ marginTop: "12px" }}>
          <Table stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    style={{ minWidth: column.minWidth }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            {getAllProductsLoading === "PENDING" ? (
              <Box sx={{ width: "100%" }}>
                <LinearProgress />
              </Box>
            ) : (
              <TableBody>
                {allDefinedProducts
                  ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((article) => {
                    return (
                      <TableRow
                        hover
                        role="checkbox"
                        tabIndex={-1}
                        key={article.objectId}
                      >
                        {columns.map((column) => {
                          const value =
                            column.id === "action"
                              ? article
                              : article[column.id];
                          return getColumn(value, column);
                        })}
                      </TableRow>
                    );
                  })}
              </TableBody>
            )}
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={allDefinedProducts?.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
        {showAddNewArticleModal && (
          <AddEditProductModal
            open={showAddNewArticleModal}
            onClose={handleOnCloseAddNewArticleModal}
            productToEdit={selectedProductToEdit}
            isEditMode={selectedProductToEdit}
            onSuccessAddNewProduct={handleSuccessAddNewProduct}
            onSuccessEditProduct={handleSuccessEditProduct}
          />
        )}
        {showDeleteWraningModal && (
          <AlertDialogComponent
            open={setShowDeleteWarningModal}
            onClose={handleOnCloseWarningModal}
            onConfirm={onConfirmDeleteProduct}
            title={t("adminSettings.deleteProductWarning")}
          />
        )}
      </Paper>
    </>
  );
};
export default ArticlesSettingTable;
